// Draft vault — mirrors the pilot's WORK (slip/expense drafts, voyage-plan day
// notes, unverified marks, pending submits, workspace state) into the same
// IndexedDB durable store that protects the login, and restores it when iOS
// discards localStorage. The login got this protection on 2026-07-11; the
// drafts happened to survive that wipe, but nothing guarantees they will next
// time, and losing a half-finished pilot slip is worse than losing a login.
//
// How it works:
// - restoreDraftVault() runs BEFORE the app renders: any vaulted key missing
//   from localStorage is copied back. localStorage always wins when present —
//   it stays the authoritative, synchronous store; the vault only fills gaps.
// - installDraftVault() then patches localStorage.setItem/removeItem so every
//   later write/delete of a vaulted key is mirrored automatically — no editor
//   needs to know the vault exists, and new save sites are covered by default.
//   It also sweeps existing keys once, so devices updating to this version get
//   their current drafts vaulted immediately.
// - Deletions mirror too, so tombstones (pilotSlipDeleted_*) behave and a
//   deleted draft can't resurrect from the vault.
// - Caches (vessel lists, roles) and auth tokens are deliberately NOT vaulted:
//   caches rebuild from the network, and the login has its own backup.
//   EXCEPTION: noaa_ tide/current responses ARE vaulted — they can't rebuild
//   when NOAA itself is down (SE Alaska outage 2026-07-18), and a plan opened
//   on the bridge needs them. They're pruned when trips age out, so the
//   mirrored deletes keep the vault from growing.

import { durableEntries, durableSet, durableRemove } from "./durableStore";

const VAULT_PREFIXES = [
  "pilotSlipDraft_",
  "pilotSlipDeleted_",
  "pilotSlipImported_",
  "pilotSlipPending_",
  "pilotSlipUnsaved_",
  "submitQueue_",
  "handoffFrom_",
  "handoffRow_",
  "expenseReport_",
  "voyagePlanDayNote_",
  "voyagePlanBrief_",
  "slipSegMarks_",
  "slipSegHints_",
  "slipPilotMarks_",
  "slipPilotHints_",
  "slipPilotCount_",
  "wsOwner_",
  "wsTab_",
  "noaa_",
];

const VAULT_EXACT = new Set([
  "pilotSlipSubmittedIndex",
  "workspaceArchivedTrips",
  "dismissedExpenseDrafts",
  "workspaceLast",
  "currentTripId",
  "lastRoute",
]);

function isVaulted(key) {
  if (typeof key !== "string") return false;
  if (key.endsWith("_prev")) return false; // displaced-copy backups stay device-local
  return VAULT_EXACT.has(key) || VAULT_PREFIXES.some((p) => key.startsWith(p));
}

// Copy vaulted keys back into localStorage when they're missing (iOS wiped or
// pruned it). Must complete before anything reads localStorage — main.jsx
// awaits it before rendering the app.
export async function restoreDraftVault() {
  try {
    const entries = await durableEntries();
    let restored = 0;
    for (const [key, value] of entries) {
      if (!isVaulted(key) || typeof value !== "string") continue;
      try {
        if (localStorage.getItem(key) == null) {
          localStorage.setItem(key, value);
          restored++;
        }
      } catch {
        break; // quota mid-restore — a partial restore still beats none
      }
    }
    if (restored > 0) {
      console.warn(`draftVault: restored ${restored} item(s) that localStorage had lost`);
    }
  } catch { /* vault is best-effort — the app must open regardless */ }
}

// Mirror every future write/removal of vaulted keys, and sweep what's already
// there (first run on an updated device vaults its existing drafts).
export function installDraftVault() {
  try {
    const origSet = Storage.prototype.setItem;
    const origRemove = Storage.prototype.removeItem;
    Storage.prototype.setItem = function (key, value) {
      origSet.call(this, key, value);
      if (this === window.localStorage && isVaulted(key)) {
        try { durableSet(key, String(value)); } catch { /* mirror is best-effort */ }
      }
    };
    Storage.prototype.removeItem = function (key) {
      origRemove.call(this, key);
      if (this === window.localStorage && isVaulted(key)) {
        try { durableRemove(key); } catch { /* mirror is best-effort */ }
      }
    };
  } catch { /* patching failed — vault becomes sweep-only */ }

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !isVaulted(key)) continue;
      const value = localStorage.getItem(key);
      if (value != null) durableSet(key, value);
    }
  } catch { /* ignore */ }

  // Sentinel: proof that localStorage survived. Boot reads this to decide
  // whether it can skip the pre-paint restore (see main.jsx). Device-local on
  // purpose — NOT vaulted, so an iOS storage wipe takes it too, which is
  // exactly the signal that a restore is actually needed.
  try { localStorage.setItem("seapa_vault_ok", "1"); } catch { /* ignore */ }
}
